import React, { useEffect, useRef, useState } from 'react'
import Icon from '../Icon'

const clamp = (v, a, b) => Math.max(a, Math.min(b, v))
let rid = 0

// Lets the user draw / move / resize rectangles over an image, and (for the
// watermark tool) pick a colour with a live red "what-will-be-removed" overlay.
// Regions are page-relative ratios { xRatio, yRatio, wRatio, hRatio }.
function RegionEditor({ file, regions, onChange, hint, onPickColor, color, tolerance }) {
  const [url, setUrl] = useState(null)
  const [pickAt, setPickAt] = useState(null)
  const [ready, setReady] = useState(false)
  const wrapRef = useRef(null)
  const maskRef = useRef(null)
  const draftRef = useRef(null)
  const dragRef = useRef(null)
  const sampleRef = useRef(null)
  const [draft, setDraft] = useState(null)

  useEffect(() => {
    const u = URL.createObjectURL(file)
    setUrl(u)
    setReady(false)
    const im = new Image()
    im.onload = () => {
      const c = document.createElement('canvas')
      c.width = im.naturalWidth
      c.height = im.naturalHeight
      const cx = c.getContext('2d', { willReadFrequently: true })
      cx.drawImage(im, 0, 0)
      sampleRef.current = { cx, canvas: c, w: c.width, h: c.height }
      setReady(true)
    }
    im.src = u
    return () => URL.revokeObjectURL(u)
  }, [file])

  const sampleColor = (xRatio, yRatio) => {
    const s = sampleRef.current
    if (!s) return null
    const x = Math.min(s.w - 1, Math.max(0, Math.floor(xRatio * s.w)))
    const y = Math.min(s.h - 1, Math.max(0, Math.floor(yRatio * s.h)))
    const p = s.cx.getImageData(x, y, 1, 1).data
    return { r: p[0], g: p[1], b: p[2] }
  }

  // Live preview of which pixels the colour-key will remove (red overlay).
  useEffect(() => {
    const cv = maskRef.current
    const s = sampleRef.current
    const wrap = wrapRef.current
    if (!cv || !wrap) return
    const rect = wrap.getBoundingClientRect()
    if (!rect.width) return
    cv.width = Math.round(rect.width)
    cv.height = Math.round(rect.height)
    const ctx = cv.getContext('2d')
    ctx.clearRect(0, 0, cv.width, cv.height)
    if (!color || !s) return

    // Downscale the source to display size to read pixels quickly.
    const tmp = document.createElement('canvas')
    tmp.width = cv.width
    tmp.height = cv.height
    const tctx = tmp.getContext('2d', { willReadFrequently: true })
    tctx.drawImage(s.canvas, 0, 0, cv.width, cv.height)
    const src = tctx.getImageData(0, 0, cv.width, cv.height).data
    const out = ctx.createImageData(cv.width, cv.height)
    const op = out.data
    const tol2 = (tolerance ?? 60) * (tolerance ?? 60)
    for (let i = 0; i < src.length; i += 4) {
      const dr = src[i] - color.r
      const dg = src[i + 1] - color.g
      const db = src[i + 2] - color.b
      if (dr * dr + dg * dg + db * db <= tol2) {
        op[i] = 255
        op[i + 1] = 40
        op[i + 2] = 60
        op[i + 3] = 150
      }
    }
    ctx.putImageData(out, 0, 0)
  }, [color, tolerance, ready, regions])

  const rel = (e) => {
    const r = wrapRef.current.getBoundingClientRect()
    return { x: clamp((e.clientX - r.left) / r.width, 0, 1), y: clamp((e.clientY - r.top) / r.height, 0, 1), rect: r }
  }

  const onPointerDown = (e) => {
    if (e.target !== wrapRef.current && e.target.tagName !== 'IMG' && e.target.tagName !== 'CANVAS') return
    const { x, y } = rel(e)
    draftRef.current = { x, y }
    setDraft({ xRatio: x, yRatio: y, wRatio: 0, hRatio: 0 })
    wrapRef.current.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e) => {
    if (draftRef.current) {
      const { x, y } = rel(e)
      const s = draftRef.current
      setDraft({ xRatio: Math.min(s.x, x), yRatio: Math.min(s.y, y), wRatio: Math.abs(x - s.x), hRatio: Math.abs(y - s.y) })
      return
    }
    const d = dragRef.current
    if (!d) return
    const dx = (e.clientX - d.startX) / d.rect.width
    const dy = (e.clientY - d.startY) / d.rect.height
    onChange(regions.map((r) => {
      if (r.id !== d.id) return r
      if (d.mode === 'move') return { ...r, xRatio: clamp(d.orig.xRatio + dx, 0, 1 - r.wRatio), yRatio: clamp(d.orig.yRatio + dy, 0, 1 - r.hRatio) }
      return { ...r, wRatio: clamp(d.orig.wRatio + dx, 0.02, 1 - r.xRatio), hRatio: clamp(d.orig.hRatio + dy, 0.02, 1 - r.yRatio) }
    }))
  }

  const onPointerUp = () => {
    if (draftRef.current) {
      const d = draft
      draftRef.current = null
      setDraft(null)
      if (d && d.wRatio > 0.02 && d.hRatio > 0.02) {
        onChange([...regions, { id: ++rid, ...d }])
      } else if (onPickColor && d) {
        const c = sampleColor(d.xRatio, d.yRatio)
        if (c) {
          onPickColor(c)
          setPickAt({ x: d.xRatio, y: d.yRatio })
        }
      }
    }
    dragRef.current = null
  }

  const startDrag = (r, mode) => (e) => {
    e.stopPropagation()
    dragRef.current = { id: r.id, mode, rect: wrapRef.current.getBoundingClientRect(), startX: e.clientX, startY: e.clientY, orig: { ...r } }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const removeRegion = (id) => (e) => {
    e.stopPropagation()
    onChange(regions.filter((r) => r.id !== id))
  }

  return (
    <div style={{ background: '#1f2430', padding: 16 }}>
      <div
        ref={wrapRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{ position: 'relative', width: '100%', maxWidth: 560, margin: '0 auto', lineHeight: 0, userSelect: 'none', touchAction: 'none', cursor: 'crosshair' }}
      >
        {url && <img src={url} alt="edit" draggable={false} style={{ width: '100%', display: 'block', borderRadius: 8 }} />}

        {/* live mask overlay (colour mode) */}
        <canvas ref={maskRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', borderRadius: 8 }} />

        {pickAt && (
          <span style={{ position: 'absolute', left: `${pickAt.x * 100}%`, top: `${pickAt.y * 100}%`, width: 16, height: 16, marginLeft: -8, marginTop: -8, borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 0 0 2px var(--c), 0 1px 4px rgba(0,0,0,.4)', pointerEvents: 'none' }} />
        )}

        {[...regions, ...(draft ? [{ id: 'draft', ...draft }] : [])].map((r) => (
          <div key={r.id}
            onPointerDown={r.id === 'draft' ? undefined : startDrag(r, 'move')}
            style={{ position: 'absolute', left: `${r.xRatio * 100}%`, top: `${r.yRatio * 100}%`, width: `${r.wRatio * 100}%`, height: `${r.hRatio * 100}%`,
              border: '2px solid var(--c)', background: 'rgba(79,70,229,.18)', cursor: 'move', boxSizing: 'border-box' }}>
            {r.id !== 'draft' && (
              <>
                <span onPointerDown={removeRegion(r.id)} style={{ position: 'absolute', right: -10, top: -10, width: 20, height: 20, borderRadius: '50%', background: 'var(--pdf)', color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', border: '2px solid #fff' }}>
                  <Icon name="x" size={11} stroke={2.5} />
                </span>
                <span onPointerDown={startDrag(r, 'resize')} style={{ position: 'absolute', right: -8, bottom: -8, width: 16, height: 16, borderRadius: '50%', background: 'var(--c)', border: '2px solid #fff', cursor: 'nwse-resize' }} />
              </>
            )}
          </div>
        ))}
      </div>
      <p style={{ color: '#cbd5e1', fontSize: 13, textAlign: 'center', marginTop: 12 }}>
        {hint || 'Drag on the image to draw a box. Add as many as you like.'}
      </p>
    </div>
  )
}

export default RegionEditor
